import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  CheckCircle,
  PendingActions,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface ReportItem {
  reportId: string;
  reporterId: string;
  reporterFullname: string;
  reportType: string;
  reportDescription: string;
  isResolved: boolean;
  createdAt: string;
}

interface ReportStats {
  totalReports: number;
  pendingReports: number;
  resolvedReports: number;
}

const UserReports: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [stats, setStats] = useState<ReportStats>({ totalReports: 0, pendingReports: 0, resolvedReports: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalReports, setTotalReports] = useState(0);

  useEffect(() => {
    if (userId) {
      fetchUserReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, userId]);

  const fetchUserReports = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getUserReports(userId!, {
        page: page + 1,
        limit: rowsPerPage,
      });

      if (response.status) {
        setReports(response.data?.reports || []);
        setStats(response.data?.stats || { totalReports: 0, pendingReports: 0, resolvedReports: 0 });
        setTotalReports(response.data?.pagination?.totalReports || 0);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Raporlar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getReportTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      USER: 'Kullanıcı',
      POST: 'Gönderi',
      MESSAGE: 'Mesaj',
      COMMENT: 'Yorum',
      SUB_COMMENT: 'Alt Yorum',
      SPAM: 'Spam',
      GENERAL: 'Genel',
      OTHER: 'Diğer',
    };
    return types[type] || type;
  };

  const getReportTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' } = {
      USER: 'error',
      POST: 'warning',
      MESSAGE: 'info',
      COMMENT: 'primary',
      SUB_COMMENT: 'primary',
      SPAM: 'error',
      GENERAL: 'default',
      OTHER: 'default',
    };
    return colors[type] || 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate(`/admin/users/${userId}`)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            Kullanıcı Raporları
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bu kullanıcı hakkında yapılan tüm raporlar
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {stats.totalReports}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Rapor
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {stats.pendingReports}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bekleyen Raporlar
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.resolvedReports}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Çözümlenen Raporlar
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell>Raporlayan</TableCell>
                <TableCell>Rapor Tipi</TableCell>
                <TableCell>Açıklama</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Tarih</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      Bu kullanıcı hakkında rapor bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow
                    key={report.reportId}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {report.reporterFullname?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {report.reporterFullname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {report.reporterId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getReportTypeLabel(report.reportType)}
                        color={getReportTypeColor(report.reportType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {report.reportDescription}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {report.isResolved ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Çözümlendi"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<PendingActions />}
                          label="Beklemede"
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(report.createdAt).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/admin/reports/${report.reportId}`)}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalReports}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>
    </Container>
  );
};

export default UserReports;

